import { BehaviorSubject, Subject, concat, from, of } from 'rxjs';
import { map, filter, scan, share } from 'rxjs/operators';
const {
  parseRawToolResultContent,
  parseToolResultContent,
  textFromFileResult,
  fileTextFromToolMessage,
  isStoredToolResultPartial
} = require('./ToolResultContent.js')

const asyncToolSessionId = value =>
  value && typeof value === 'object' && value.session_id !== undefined && value.session_id !== null
    ? String(value.session_id)
    : ''

const parseToolCallArguments = call => {
  try {
    return JSON.parse((call && call.function && call.function.arguments) || '{}')
  } catch (_err) {
    return {}
  }
}

/**
 * CanvasInterpreter - Core class that observes the transcript stream
 * and emits editor commands as an Observable
 */
class CanvasInterpreter {

  constructor(options = {}) {
    // Subject for emitting editor commands
    this.commandSubject = new Subject();
    this.hydrateToolResult = options.hydrateToolResult
        
    
    // Current available files
    this.filesSubject = new BehaviorSubject([]);
  }

  // Internal file state management
  fileStates = new Map();
  webBrowserStates = new Map();
  sessionOrigins = new Map();
  fileHydrations = new Map();

  commands = []
  sendCommand = command => {
    this.commands.push(command)
    this.commandSubject.next(command)
  }

  observeCommands = () => {
    return concat(from(this.commands), this.commandSubject)
  }
  
  /**
   * Process the transcript observable to extract and handle tool calls
   * @param {Observable} transcript$ - Observable of messages
   */
  processTranscript(transcript$) {
    // Subscribe to the transcript and process messages
    return transcript$.subscribe(event => {
      const message = event && event.message
      if (!message) return
      if (message.tool_calls && Array.isArray(message.tool_calls)) {
        message.tool_calls.forEach(call => this.processToolCall(event.ts, call));
      } else if (message.role === 'tool') {
        this.processToolResult(event)
      }
    })
  }


  /**
   * Process a tool call and emit appropriate commands
   * @param {Object} functionCall - Function call object
   */
  toProcess = {}
  processed = {}
  processToolResult = event => {
    const message = event && event.message
    if (!message) return
    const { tool_call_id } = message
    const ts = event.ts
    const call = this.toProcess[tool_call_id]
    const rawResult = parseRawToolResultContent(message)
    const resultSessionId = asyncToolSessionId(rawResult)
    if (call) {
      if (call.function && call.function.name === 'poll_tool_output') {
        const args = parseToolCallArguments(call)
        const sessionId = resultSessionId || (args.session_id !== undefined && args.session_id !== null ? String(args.session_id) : '')
        const origin = sessionId && this.sessionOrigins.get(sessionId)
        if (origin) {
          this.processToolCall(ts, origin, message, event)
          return
        }
      } else if (resultSessionId) {
        this.sessionOrigins.set(resultSessionId, call)
      }
      this.processToolCall(ts, call, message, event)
      return
    }
    if (resultSessionId) {
      const origin = this.sessionOrigins.get(resultSessionId)
      if (origin) {
        this.processToolCall(ts, origin, message, event)
      }
    }
  }

  reset = () => {
    this.toProcess = {}
    this.processed = {}
    this.fileStates = new Map()
    this.webBrowserStates = new Map()
    this.sessionOrigins = new Map()
    this.fileHydrations = new Map()
    this.commands = []
    this.sendCommand({
      type: "RESET"
    })
  }

  processToolCall = (ts, functionCall, result, resultEvent) =>{
    const { id } = functionCall
    this.toProcess[id] = functionCall
    if (ts === undefined) {
      //debugger
    }
    const { name, arguments: argsString } = functionCall.function;
    let args;
    try {
      args = JSON.parse(argsString || '{}');
    } catch (e) {
      console.log('Failed to parse tool call arguments:', e);
      return;
    }
    switch (name) {
      case 'evaluate':
      case 'navigate':
      case 'goBack':
      case 'goForward':
      case 'close':
        {
          let { url, target } = args
          if (url) {
            this.webBrowserStates.set(target, url)
            this.sendCommand({
              ts,
              type: 'WEB_PAGE_NAVIGATE',
              target,
              url
            })
          }
        }
        break;
      case "read_multiple_text_files":
        // Emit read command with current content
        if (result) {
          const {results, errors } = parseToolResultContent(result) || {}
          if (results) for (const path in results) {
            const content = textFromFileResult(results[path])
            this.fileStates.set(path, content)
            this.sendCommand({
              ts,
              type: 'UPDATE_FILE',
              path: path,
              content
          });
          }
          this.updateAvailableFiles();
        }
        break;
      case "read_text_file":
        // Emit read command with current content
        if (result) {
          const content = fileTextFromToolMessage(result)
          if (isStoredToolResultPartial(result) && this.hydrateToolResult) {
            this.fileHydrations.set(args.path, {
              ts,
              functionCall,
              result,
              resultEvent
            })
          } else {
            this.fileHydrations.delete(args.path)
          }
          this.fileStates.set(args.path, content)
          this.sendCommand({
            ts,
            type: 'UPDATE_FILE',
            path: args.path,
            content: content
          });
          this.updateAvailableFiles();
        }
        break;
        
      case "write_text_file":
        this.fileHydrations.delete(args.path)
        const isNewFile = !this.fileStates.has(args.path);
        
        // Update internal state
        this.fileStates.set(args.path, args.content);
        
        // If this is a new file, emit file creation command
        if (isNewFile) {
          this.sendCommand({
            ts,
            type: 'CREATE_FILE',
            path: args.path,
            content: args.content
          });
          
          // Update available files
          this.updateAvailableFiles();
        } else {
          // Otherwise emit update command
          this.sendCommand({
            ts,
            type: 'UPDATE_FILE',
            path: args.path,
            content: args.content
          });
        }
        break;
        
      case "edit_text_file":
        this.fileHydrations.delete(args.path)
        let oldContent = this.fileStates.get(args.path) || '';
        if (!oldContent) {
          console.error(`Cannot edit non-existent file: ${args.path}`);
          return;
        }

        if (typeof oldContent !== 'string') {
          oldContent = JSON.stringify(oldContent)
        }
        
        const newContent = oldContent.replace(args.old_string, args.new_string);
        
        // Update internal state
        this.fileStates.set(args.path, newContent);
        
        // Emit update command
        this.sendCommand({
          ts,
          type: 'UPDATE_FILE',
          path: args.path,
          content: newContent
        });
        this.updateAvailableFiles();
        break;
    }
  }

  hydrateFile = async path => {
    const pending = this.fileHydrations.get(path)
    if (!pending || pending.loading || !this.hydrateToolResult) return
    pending.loading = true
    try {
      const event = await this.hydrateToolResult(pending.resultEvent)
      const result = event && event.message
      if (!result || isStoredToolResultPartial(result)) return
      this.fileHydrations.delete(path)
      this.processToolCall(pending.ts, pending.functionCall, result, event)
    } finally {
      pending.loading = false
    }
  }

  /**
   * Update available files list
   */
  updateAvailableFiles() {
    const files = Array.from(this.fileStates.keys());
    this.filesSubject.next(files);
  }

  getFileContents(file) {
    return this.fileStates.get(file)
  }


  /**
   * Get observable of available files
   * @returns {Observable} - Observable of file paths
   */
  observeAvailableFiles() {
    const files = Array.from(this.fileStates.keys())
    return concat(of(files), this.filesSubject)
  }

  /**
   * Get the commands observable
   * @returns {Observable} - Observable of editor commands
   */
  getCommands$() {
    return this.commands$;
  }

  /**
   * Clean up resources
   */
  dispose() {
    this.fileStates.clear();
    this.fileHydrations.clear();
    this.commandSubject.complete();
    this.filesSubject.complete();
  }
}

export default CanvasInterpreter;
